import type { CountryCode, PresetTopic } from '../types';

/**
 * Quiz preset topics organized by country
 * Total: 48 topics (6 per country × 8 countries)
 */
export const PRESET_TOPICS: Record<CountryCode, PresetTopic[]> = {
  KR: [
    { topicId: 'KR-fractions', label: '분수에 대한 문제를 만들어줘' },
    { topicId: 'KR-geography', label: '지리에 대한 문제를 만들어줘' },
    { topicId: 'KR-grammar', label: '국어 문법에 대한 문제를 만들어줘' },
    {
      topicId: 'KR-joseon',
      label: '조선 초기 상황과 특징에 대한 문제를 만들어줘',
    },
    { topicId: 'KR-photosynthesis', label: '광합성에 대한 문제를 만들어줘' },
    {
      topicId: 'KR-winter-safety',
      label: '겨울 안전 수칙에 대한 문제를 만들어줘',
    },
  ],
  JP: [
    {
      topicId: 'JP-earthquake-safety',
      label: '地震の安全対策についての問題を作って',
    },
    { topicId: 'JP-edo-period', label: '江戸時代についての問題を作って' },
    { topicId: 'JP-fractions', label: '分数についての問題を作って' },
    { topicId: 'JP-geography', label: '地理についての問題を作って' },
    { topicId: 'JP-particles', label: '助詞についての問題を作って' },
    { topicId: 'JP-photosynthesis', label: '光合成についての問題を作って' },
  ],
  TH: [
    {
      topicId: 'TH-fractions',
      label: 'สร้างโจทย์เกี่ยวกับเศษส่วน',
      labelEn: 'Create problems about fractions',
    },
    {
      topicId: 'TH-grammar',
      label: 'สร้างโจทย์เกี่ยวกับไวยากรณ์',
      labelEn: 'Create problems about grammar',
    },
    {
      topicId: 'TH-mangrove',
      label: 'สร้างโจทย์เกี่ยวกับป่าชายเลน',
      labelEn: 'Create problems about mangrove forests',
    },
    {
      topicId: 'TH-morality',
      label: 'สร้างโจทย์เกี่ยวกับศีลธรรม',
      labelEn: 'Create problems about morality',
    },
    {
      topicId: 'TH-sukhothai',
      label: 'สร้างโจทย์เกี่ยวกับสมัยสุโขทัย',
      labelEn: 'Create problems about Sukhothai Kingdom',
    },
    {
      topicId: 'TH-summer-safety',
      label: 'สร้างโจทย์เกี่ยวกับความปลอดภัยในฤดูร้อน',
      labelEn: 'Create problems about summer safety',
    },
  ],
  ID: [
    {
      topicId: 'ID-cultural-diversity',
      label: 'Buat soal tentang keberagaman budaya',
    },
    { topicId: 'ID-fractions', label: 'Buat soal tentang pecahan' },
    { topicId: 'ID-grammar', label: 'Buat soal tentang tata bahasa' },
    { topicId: 'ID-majapahit', label: 'Buat soal tentang Kerajaan Majapahit' },
    { topicId: 'ID-rainforest', label: 'Buat soal tentang hutan hujan tropis' },
    {
      topicId: 'ID-rainy-safety',
      label: 'Buat soal tentang keselamatan musim hujan',
    },
  ],
  VN: [
    {
      topicId: 'VN-cultural-diversity',
      label: 'Tạo bài tập về đa dạng văn hóa',
      labelEn: 'Create problems about cultural diversity',
    },
    {
      topicId: 'VN-flood-safety',
      label: 'Tạo bài tập về an toàn lũ lụt',
      labelEn: 'Create problems about flood safety',
    },
    {
      topicId: 'VN-fractions',
      label: 'Tạo bài tập về phân số',
      labelEn: 'Create problems about fractions',
    },
    {
      topicId: 'VN-grammar',
      label: 'Tạo bài tập về ngữ pháp',
      labelEn: 'Create problems about grammar',
    },
    {
      topicId: 'VN-history',
      label: 'Tạo bài tập về lịch sử Việt Nam',
      labelEn: 'Create problems about Vietnam history',
    },
    {
      topicId: 'VN-rainforest',
      label: 'Tạo bài tập về rừng nhiệt đới',
      labelEn: 'Create problems about rainforest',
    },
  ],
  PH: [
    {
      topicId: 'PH-asean',
      label: 'Gumawa ng mga tanong tungkol sa ASEAN',
      labelEn: 'Create problems about ASEAN',
    },
    {
      topicId: 'PH-coral-reef',
      label: 'Gumawa ng mga tanong tungkol sa bahura',
      labelEn: 'Create problems about coral reef',
    },
    {
      topicId: 'PH-fractions',
      label: 'Gumawa ng mga tanong tungkol sa fraction',
      labelEn: 'Create problems about fractions',
    },
    {
      topicId: 'PH-grammar',
      label: 'Gumawa ng mga tanong tungkol sa balarila',
      labelEn: 'Create problems about grammar',
    },
    {
      topicId: 'PH-rizal-revolution',
      label: 'Gumawa ng mga tanong tungkol sa Rizal at Rebolusyon',
      labelEn: 'Create problems about Rizal & Revolution',
    },
    {
      topicId: 'PH-typhoon-safety',
      label: 'Gumawa ng mga tanong tungkol sa kaligtasan sa bagyo',
      labelEn: 'Create problems about typhoon safety',
    },
  ],
  MY: [
    {
      topicId: 'MY-cultural-diversity',
      label: 'Buat soalan tentang kepelbagaian budaya',
      labelEn: 'Create problems about cultural diversity',
    },
    {
      topicId: 'MY-fractions',
      label: 'Buat soalan tentang pecahan',
      labelEn: 'Create problems about fractions',
    },
    {
      topicId: 'MY-grammar',
      label: 'Buat soalan tentang tatabahasa',
      labelEn: 'Create problems about grammar',
    },
    {
      topicId: 'MY-heat-safety',
      label: 'Buat soalan tentang keselamatan cuaca panas',
      labelEn: 'Create problems about heat safety',
    },
    {
      topicId: 'MY-melaka',
      label: 'Buat soalan tentang Kesultanan Melaka',
      labelEn: 'Create problems about Malacca Sultanate',
    },
    {
      topicId: 'MY-rainforest',
      label: 'Buat soalan tentang hutan hujan',
      labelEn: 'Create problems about rainforest',
    },
  ],
  US: [
    {
      topicId: 'US-american-revolution',
      label: 'Create problems about American Revolution',
    },
    { topicId: 'US-fractions', label: 'Create problems about fractions' },
    {
      topicId: 'US-government',
      label: 'Create problems about government & democracy',
    },
    { topicId: 'US-grammar', label: 'Create problems about grammar' },
    { topicId: 'US-water-cycle', label: 'Create problems about water cycle' },
    {
      topicId: 'US-winter-safety',
      label: 'Create problems about winter safety',
    },
  ],
};

/**
 * Get preset topics for a given country code
 *
 * @param countryCode - ISO country code from useCountryCode()
 * @returns Array of preset topics for that country, or US presets as fallback
 *
 * @example
 * const presets = getPresetsForCountry('KR'); // Returns 6 Korean topics
 * const presets = getPresetsForCountry('DE'); // Returns 6 US topics (fallback)
 */
export function getPresetsForCountry(countryCode: string): PresetTopic[] {
  if (countryCode in PRESET_TOPICS) {
    return PRESET_TOPICS[countryCode as CountryCode];
  }

  // Fallback to US presets for unsupported countries
  return PRESET_TOPICS.US;
}
